import React, { useEffect, useRef } from "react";
import { Tabs, TabsList, TabsTrigger } from "@/app/components/ui/tabs";

export interface PrimaryTab {
  value: string;
  label: string;
}

export interface SecondaryFilter {
  value: string;
  label: string;
}

interface ModuleTabNavigationProps {
  primaryTabs: PrimaryTab[];
  activePrimaryTab: string;
  onPrimaryTabChange: (value: string) => void;
  secondaryFilters?: SecondaryFilter[];
  activeSecondaryFilter?: string;
  onSecondaryFilterChange?: (value: string) => void;
  secondaryRightContent?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * ModuleTabNavigation
 *
 * Two-tier navigation pattern with responsive behavior:
 * - Primary tabs row scrolls horizontally when labels overflow. A subtle
 *   right-edge fade indicates "more content".
 * - On mount AND on active-tab change, scrolls the active tab into view —
 *   so the default-active tab isn't pinned left while later tabs clip off
 *   the right.
 * - Font size, gaps, and padding shrink on mobile to minimize how often
 *   overflow happens in the first place.
 * - Secondary tier stacks filter pills above right-content (e.g. search)
 *   below `md:` so the search input gets its own full-width row.
 */
export function ModuleTabNavigation({
  primaryTabs,
  activePrimaryTab,
  onPrimaryTabChange,
  secondaryFilters,
  activeSecondaryFilter,
  onSecondaryFilterChange,
  secondaryRightContent,
  children,
}: ModuleTabNavigationProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Scroll the active tab into view on mount AND whenever it changes.
  //
  // Why mount matters: on mobile, the first tab is usually active by
  // default, so it sits at the left edge of the scroll container and the
  // last tab ("Action Log") clips off the right. The user never saw a
  // change event, so a change-only effect never fires. Running this on
  // mount makes the page take stock of its own width and reveal whichever
  // tab is currently active, preserving affordance.
  //
  // A tiny RAF delay lets layout settle after mount so the container has
  // its final scrollWidth before we measure.
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const raf = requestAnimationFrame(() => {
      const activeEl = container.querySelector<HTMLElement>(
        `[data-state="active"]`
      );
      if (!activeEl) return;

      // `nearest` so already-visible tabs don't jump around unnecessarily,
      // but clipped ones get revealed. We check ourselves first — if the
      // element is fully in view, skip the call entirely to avoid smooth-
      // scroll flicker on already-visible tabs.
      const cRect = container.getBoundingClientRect();
      const aRect = activeEl.getBoundingClientRect();
      const fullyVisible =
        aRect.left >= cRect.left && aRect.right <= cRect.right;
      if (fullyVisible) return;

      activeEl.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    });

    return () => cancelAnimationFrame(raf);
  }, [activePrimaryTab]);

  return (
    <Tabs value={activePrimaryTab} onValueChange={onPrimaryTabChange} className="w-full">
      <div
        className="module-tab-primary-wrap border-b relative"
        style={{
          borderColor: "var(--border)",
          backgroundColor: "var(--background)",
        }}
      >
        <style>{`
          .module-tab-primary-scroll::-webkit-scrollbar { display: none; }
          .module-tab-filter-scroll::-webkit-scrollbar { display: none; }

          /* Lighter right-edge fade — indicates horizontal scroll without
             making the last tab label unreadable. Narrower (20px) and
             starts the gradient later so partial letters still show
             through clearly. Hidden at md+ where overflow is unlikely. */
          .module-tab-primary-wrap::after {
            content: '';
            position: absolute;
            top: 0;
            bottom: 1px;
            right: 0;
            width: 20px;
            pointer-events: none;
            background: linear-gradient(
              to right,
              rgba(0, 0, 0, 0),
              var(--background) 100%
            );
            z-index: 1;
          }
          @media (min-width: 768px) {
            .module-tab-primary-wrap::after { display: none; }
          }

          .module-tab-trigger {
            position: relative;
            color: var(--muted-foreground);
            transition: color 0.2s;
          }
          .module-tab-trigger[data-state="active"] {
            color: var(--foreground);
            font-weight: var(--font-weight-bold);
          }
          .module-tab-trigger[data-state="active"]::after {
            content: '';
            position: absolute;
            bottom: -1px;
            left: 0;
            right: 0;
            height: 4px;
            background-color: var(--alpha-teal);
          }
        `}</style>

        <div
          ref={scrollRef}
          className="module-tab-primary-scroll"
          style={{
            overflowX: "auto",
            overflowY: "hidden",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            scrollBehavior: "smooth",
          }}
        >
          <TabsList
            // Tight mobile spacing so 3 tabs fit before scroll kicks in:
            //   pl-4 pr-6 (16 + 24 = 40px)  -> room for fade + edge
            //   gap-4 (16px between triggers) -> was gap-5 (20px)
            // On md+, back to generous px-8 + gap-8.
            className="pl-4 pr-6 md:px-8 bg-transparent border-0 h-auto p-0 gap-4 md:gap-8 w-max min-w-full"
            style={{ backgroundColor: "transparent" }}
          >
            {primaryTabs.map((tab) => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className="module-tab-trigger rounded-none px-0 pb-3 pt-4 md:pb-4 md:pt-6 bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none border-0 whitespace-nowrap shrink-0"
                style={{
                  fontWeight: "var(--font-weight-semibold)",
                  // Smaller floor on mobile (text-sm) so 3 wide labels
                  // fit a 380px viewport; grows back to text-lg on wide
                  // viewports. The 1.5vw slope is gentle enough that
                  // iPad/landscape phones land near text-base.
                  fontSize: "clamp(var(--text-sm), 1.5vw, var(--text-lg))",
                }}
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
      </div>

      {/* Secondary Filter Tier */}
      {secondaryFilters && secondaryFilters.length > 0 && (
        <div
          className="px-4 md:px-8 py-3 md:py-4 border-b flex flex-col md:flex-row md:items-center md:justify-between gap-3"
          style={{ borderColor: "var(--border)" }}
        >
          <div
            className="module-tab-filter-scroll flex items-center gap-2 -mx-1 px-1"
            style={{
              overflowX: "auto",
              overflowY: "hidden",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            {secondaryFilters.map((filter) => (
              <button
                key={filter.value}
                onClick={() => onSecondaryFilterChange?.(filter.value)}
                className="px-3 py-1.5 rounded transition-colors shrink-0 whitespace-nowrap"
                style={{
                  backgroundColor:
                    activeSecondaryFilter === filter.value
                      ? "var(--muted)"
                      : "transparent",
                  fontWeight: "var(--font-weight-normal)",
                  fontSize: "var(--text-sm)",
                  color: "var(--muted-foreground)",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {secondaryRightContent && (
            <div className="flex items-center gap-2 w-full md:w-auto md:shrink-0">
              {secondaryRightContent}
            </div>
          )}
        </div>
      )}

      {children}
    </Tabs>
  );
}